import {autoserializeAs, inheritSerialization} from "dcerialize";
import {IdOnlyBactoforceLocation, SimpleBactoforceLocation} from "../bactoforce/BactoforceLocation";
import type {Existing, IdentifiableModel, IdOf, New} from "../base/IdentifiableModel";
import {createExisting, createNew} from "../base/IdentifiableModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {ModificationDetails} from "../ModificationDetails";

export class IdOnlyThirdPartyCompany implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyThirdPartyCompany>): Existing<IdOnlyThirdPartyCompany> {
    return createExisting(IdOnlyThirdPartyCompany, id);
  }
}

@inheritSerialization(() => IdOnlyThirdPartyCompany)
export class SimpleThirdPartyCompany extends IdOnlyThirdPartyCompany implements SoftDeletableModel {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
}

@inheritSerialization(() => SimpleThirdPartyCompany)
export class WritableThirdPartyCompany extends SimpleThirdPartyCompany {
  @autoserializeAs(() => String)
  public street: string;
  @autoserializeAs(() => String)
  public zip: string;
  @autoserializeAs(() => String)
  public city: string;
  @autoserializeAs(() => IdOnlyBactoforceLocation)
  public bactoforceLocation: Existing<IdOnlyBactoforceLocation>;

  public static createNew(name: string, street: string, zip: string, city: string,
                          bactoforceLocation: Readonly<Existing<IdOnlyBactoforceLocation>>): New<WritableThirdPartyCompany> {
    const company = createNew(WritableThirdPartyCompany);
    company.name = name;
    company.street = street;
    company.zip = zip;
    company.city = city;
    company.bactoforceLocation = bactoforceLocation;
    return Object.assign(company, {isDeleted: false});
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableThirdPartyCompany)
export class ThirdPartyCompany extends WritableThirdPartyCompany {
  @autoserializeAs(() => SimpleBactoforceLocation)
  public override bactoforceLocation: Readonly<Existing<SimpleBactoforceLocation>>;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}
